import { Box } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';

import { useAuth } from '../../core/authContext';
import { Sidebar } from '../Home/Sidebar';
import { NAV_ROUTES } from '../Home/navRoutes';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { COLORS } from '../IndividualPlan/tokens';
import { TopBar, type Crumb } from '../IndividualPlan/TopBar';
import { DataNotes } from '../ProgramEvaluation/components/DataNotes';
import { ProgramEvaluationHeader } from '../ProgramEvaluation/components/ProgramEvaluationHeader';
import { ScoreUnitToggle } from '../ProgramEvaluation/components/ScoreUnitToggle';
import { ReportSections } from '../ProgramEvaluation';
import {
  ContentColumn,
  DistrictHeader,
  ToggleBar,
} from '../ProgramEvaluation/program-evaluation.styled';
import { ScoreUnitProvider } from '../ProgramEvaluation/ScoreUnitContext';
import { SampleBanner } from './SampleBanner';
import { serverSampleGateSources, useSampleGate, type SampleGateSources } from './sampleGate';
import { sampleContent, sampleReport } from './samplePeFixture';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const CRUMBS: Crumb[] = [{ label: 'Reports', to: NAV_ROUTES.reports }, { label: 'Program Evaluation' }];

export function SampleProgramEvaluationPage({
  gateSources = serverSampleGateSources,
}: {
  gateSources?: SampleGateSources;
}) {
  const { isSuperAdmin, isDistrictAdmin } = useAuth();
  const navigate = useNavigate();
  const allowed = isSuperAdmin || isDistrictAdmin;
  const gate = useSampleGate({ isSuperAdmin: isSuperAdmin || !allowed, isDistrictAdmin }, gateSources);

  if (!allowed) return <Navigate to={NAV_ROUTES.reports} replace />;
  if (gate.programEvaluation === 'pending') return null;
  if (gate.programEvaluation !== 'absent') return <Navigate to={NAV_ROUTES.reports} replace />;

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: COLORS.bg }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="reports" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar crumbs={CRUMBS} />
        <Box sx={{ flex: 1, py: 4 }}>
          <ContentColumn>
            <Box sx={{ mb: 4 }}>
              <SampleBanner
                kind="programEvaluation"
                admin
                sisConnected={gate.sisConnected}
                accessUploaded={gate.accessUploaded}
                sisMatchingConfirmed={gate.sisMatchingConfirmed}
                sisMatchingRequired={gate.sisMatchingRequired}
                onUploadAccess={() => navigate(NAV_ROUTES.data)}
              />
            </Box>
            <DistrictHeader>
              <ProgramEvaluationHeader report={sampleReport} showShare={false} />
            </DistrictHeader>
            <ScoreUnitProvider initialUnit={sampleContent.unit_mode}>
              <ToggleBar>
                <ScoreUnitToggle />
              </ToggleBar>
              <ReportSections report={sampleReport} content={sampleContent} />
            </ScoreUnitProvider>
            <DataNotes notes={sampleReport.data_quality.notes} />
          </ContentColumn>
        </Box>
      </Box>
    </Box>
  );
}
